import { CUSTOMER_STATUSES, CustomerStatus } from '@vy/types';
import { FC, useCallback } from 'react';
import { SelectChangeEvent } from '@mui/material';
import type { CustomerOverview } from '@vy/api-definition';
import { useQueryClient } from 'react-query';
import { VyStatusSelect } from '../../../../../components/new-design/inputs/VyStatusSelect/VyStatusSelect';
import { STATUS_COLORS_CONFIG } from './constants/statusColorsConfig';
import { getStatusDisplayLabel } from '../../utils/getStatusDisplayLabel';
import { queryKeys } from '../../../../../services/query-key/query-keys';
import { runMutation } from '../../../../../utils/vy-mutation';
import { useUpdateCustomer } from '../../../../../hooks/requests/use-update-customer';
import { useDialog } from '../../../../../hooks/use-dialog';
import { ActiveContractsAlertModal } from '../ActiveContractsAlertModal/ActiveContractsAlertModal';
import { useNavigate } from 'react-router-dom';

type CustomerStatusSelectProps = {
  value: CustomerStatus;
  customer: CustomerOverview;
};

const UNASSIGNABLE_CUSTOMER_STATUSES_WHEN_CUSTOMER_HAS_CONTRACTS = new Set<CustomerStatus>([
  CUSTOMER_STATUSES.Suspended,
  CUSTOMER_STATUSES.Inactive,
]);

export const CustomerStatusSelect: FC<CustomerStatusSelectProps> = ({ value, customer }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isDialogOpen, handleOpen: handleOpenAlertDialog, handleClose } = useDialog();

  const contractsCount = customer.contractsCount;

  const updateCustomerMutation = useUpdateCustomer(customer.id);
  const isCustomerHasContracts = contractsCount > 0;

  const handleChangeStatus = useCallback(
    (event: SelectChangeEvent<CustomerStatus>) => {
      const newStatus = event.target.value as CustomerStatus;
      const isUnassignableForCustomerWithContracts =
        UNASSIGNABLE_CUSTOMER_STATUSES_WHEN_CUSTOMER_HAS_CONTRACTS.has(newStatus);

      if (isCustomerHasContracts && isUnassignableForCustomerWithContracts) {
        handleOpenAlertDialog();
        return;
      }

      void runMutation(
        updateCustomerMutation,
        {
          status: newStatus,
        },
        {
          pending: 'Updating customer status...',
          success: 'Customer status updated',
          error: 'Failed to update customer status',
        },
        {
          onSuccess: async () => {
            await Promise.all([
              queryClient.invalidateQueries(queryKeys.customersOverviewsNew()),
              queryClient.invalidateQueries(queryKeys.customersCountByStatus()),
            ]);
          },
        },
      );
    },
    [isCustomerHasContracts],
  );

  const handleNavigateToCustomerContracts = useCallback(() => {
    navigate(`/customers/${customer.id}/contracts`);
  }, [customer.id]);

  return (
    <>
      <VyStatusSelect
        value={value}
        onChange={handleChangeStatus}
        colorsConfig={STATUS_COLORS_CONFIG}
        options={Object.values(CUSTOMER_STATUSES)}
        getDisplayLabel={getStatusDisplayLabel}
        disabled={updateCustomerMutation.isLoading}
      />
      <ActiveContractsAlertModal
        isOpen={isDialogOpen}
        onClose={handleClose}
        onSubmit={handleNavigateToCustomerContracts}
        contractsCount={contractsCount}
      />
    </>
  );
};
